import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { MachineTimeInput } from "./_components/MachineTimeInput";
import { DateRangeInputs } from "./_components/DateRangeInputs";
import { PartUsageFields } from "./_components/PartUsageFields";
import { PrtCustomerSelect } from "./_components/PrtCustomerSelect";
import { RrtPartPlatformSelect } from "./_components/RrtPartPlatformSelect";
import { ExportTypeCheckboxes } from "./_components/ExportTypeCheckboxes";
import { LocationSelect } from "./_components/LocationSelect";
import { ConditionSelect } from "./_components/ConditionSelect";
import {
  useApiList,
  useAllPartDetails,
  useAssetDetails,
  useCustomerOptions,
  usePartPlatforms,
  usePartUsages,
  useRepairPartUsages,
} from "@/queries";
import { AssetDetailHandler } from "./_components/handlers/AssetDetailHandler";
import { PartNonRepairableDetailHandler } from "./_components/handlers/PartNonRepairableDetailHandler";
import { PartRepairableDetailHandler } from "./_components/handlers/PartRepairableDetailHandler";
import { PrtReportingDetailHandler } from "./_components/handlers/PrtReportingDetailHandler";
import { PartUsageDetailHandler } from "./_components/handlers/PartUsageDetailHandler";
import { SotDetailHandler } from "./_components/handlers/SotDetailHandler";
import { RrtReportingDetailHandler } from "./_components/handlers/RrtReportingDetailHandler";
import { MachineOthSotDetailHandler } from "./_components/handlers/MachineOthSotDetailHandler";
import { MachinePmSotDetailHandler } from "./_components/handlers/MachinePmSotDetailHandler";
import { MachineCmSotDetailHandler } from "./_components/handlers/MachineCmSotDetailHandler";
import { AssetSummaryHandler } from "./_components/handlers/AssetSummaryHandler";
import { PartRepairableSummaryHandler } from "./_components/handlers/PartRepairableSummaryHandler";
import { PartNonRepairableSummaryHandler } from "./_components/handlers/PartNonRepairableSummaryHandler";
import { PartUsageSummaryHandler } from "./_components/handlers/PartUsageSummaryHandler";
import { PrtReportingSummaryHandler } from "./_components/handlers/PrtReportingSummaryHandler";
import { RrtReportingSummaryHandler } from "./_components/handlers/RrtReportingSummaryHandler";

function toLocationOptions(items) {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  return items.reduce((acc, obj) => {
    const key = `${obj.location_id}-${obj.location_type}`;
    if (!seen.has(key)) {
      seen.add(key);
      acc.push({
        location_id: obj.location_id,
        location_type: obj.location_type,
        value: `${obj.location_type} | ${obj.locations?.name || ""}`,
        label: `${obj.location_type} | ${obj.locations?.name || ""}`,
      });
    }
    return acc;
  }, []);
}

export function ExportModal({ currentExport, close, setCurrentExport }) {
  const [data, setData] = useState({});

  const exportToExcel = (exportData, filename) => {
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Export Data");
    XLSX.writeFile(wb, filename);
  };

  const { data: assetDetails } = useAssetDetails();
  const { data: partDetails } = useAllPartDetails();

  const { data: assets } = useApiList(
    currentExport === "asset" ? "/asset" : null,
    [currentExport],
  );
  const { data: partNonRepairables } = useApiList(
    currentExport === "part non repairable"
      ? "/part_non_repairable/index_admin"
      : null,
    [currentExport],
  );
  const { data: partRepairables } = useApiList(
    currentExport === "part repairable" ? "/part_repairable" : null,
    [currentExport],
  );
  const { data: sotList } = useApiList(
    currentExport === "sot" ? "/service_order_ticket?withSotForm=true" : null,
    [currentExport],
  );
  const { data: partUsages } = usePartUsages([currentExport]);
  const { data: repairPartUsages } = useRepairPartUsages([currentExport]);
  const { data: prtList } = useApiList(
    currentExport === "prt reporting"
      ? "/part_request_ticket?last_status_work_name=all&withPartUsages=true"
      : null,
    [currentExport],
  );
  const { data: rrtList } = useApiList(
    currentExport === "rrt reporting"
      ? "/repair_request_ticket?last_status_work_name=all"
      : null,
    [currentExport],
  );
  const { data: machines } = useApiList(
    ["Machine CM SOT", "Machine PM SOT", "Machine OTH SOT"].includes(
      currentExport,
    )
      ? "/machine?withSot=true&withCustomer=true&withServiceEngineer=true&withPartRequests=true"
      : null,
    [currentExport],
  );

  const { options: customerOptions } = useCustomerOptions();
  const { options: partPlatformOptions } = usePartPlatforms();

  const dataSave = useMemo(
    () => ({
      asset_details: assetDetails,
      part_details: partDetails,
      assets: currentExport === "asset" ? assets : undefined,
      part_non_repairables:
        currentExport === "part non repairable"
          ? partNonRepairables
          : undefined,
      part_repairables:
        currentExport === "part repairable" ? partRepairables : undefined,
      service_order_tickets: currentExport === "sot" ? sotList : undefined,
      part_usages: currentExport === "part usage" ? partUsages : undefined,
      repair_part_usages:
        currentExport === "part usage" ? repairPartUsages : undefined,
      part_request_tickets:
        currentExport === "prt reporting" ? prtList : undefined,
      repair_request_tickets:
        currentExport === "rrt reporting" ? rrtList : undefined,
      machines: [
        "Machine CM SOT",
        "Machine PM SOT",
        "Machine OTH SOT",
      ].includes(currentExport)
        ? machines
        : undefined,
    }),
    [
      currentExport,
      assetDetails,
      partDetails,
      assets,
      partNonRepairables,
      partRepairables,
      sotList,
      partUsages,
      repairPartUsages,
      prtList,
      rrtList,
      machines,
    ],
  );

  const dataOptions = useMemo(
    () => ({
      assets: currentExport === "asset" ? toLocationOptions(assets) : [],
      part_non_repairables:
        currentExport === "part non repairable"
          ? toLocationOptions(partNonRepairables)
          : [],
      part_repairables:
        currentExport === "part repairable"
          ? toLocationOptions(partRepairables)
          : [],
      customers: currentExport === "prt reporting" ? customerOptions : [],
      part_platforms:
        currentExport === "rrt reporting" ? partPlatformOptions : [],
    }),
    [
      currentExport,
      assets,
      partNonRepairables,
      partRepairables,
      customerOptions,
      partPlatformOptions,
    ],
  );

  return (
    <div className="flex flex-col gap-[20px] bg-white rounded-[8px] md:rounded-[12px] lg:rounded-[16px] p-[24px] max-h-[90dvh] overflow-y-auto overflow-x-hidden">
      <h2 className="font-bold md:w-[500px] text-[24px]">
        Export <span className="capitalize">{currentExport}</span>
      </h2>
      <MachineTimeInput
        currentExport={currentExport}
        data={data}
        setData={setData}
      />
      <DateRangeInputs
        currentExport={currentExport}
        data={data}
        setData={setData}
      />
      <PartUsageFields
        currentExport={currentExport}
        data={data}
        setData={setData}
      />
      <PrtCustomerSelect
        currentExport={currentExport}
        data={data}
        setData={setData}
        dataOptions={dataOptions}
      />
      <RrtPartPlatformSelect
        currentExport={currentExport}
        data={data}
        setData={setData}
        dataOptions={dataOptions}
      />
      <ExportTypeCheckboxes
        currentExport={currentExport}
        data={data}
        setData={setData}
      />
      {data.export_type && data.export_type == "detail" && (
        <>
          <LocationSelect
            currentExport={currentExport}
            data={data}
            setData={setData}
            dataOptions={dataOptions}
          />
          <ConditionSelect
            currentExport={currentExport}
            data={data}
            setData={setData}
            dataOptions={dataOptions}
          />
        </>
      )}
      <div className="flex flex-col md:flex-row gap-2 justify-end">
        <Button
          variant="gray"
          onClick={() => {
            setCurrentExport("");
            close();
          }}
        >
          Cancel
        </Button>
        {data.export_type && data.export_type == "detail" && (
          <>
            <AssetDetailHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <PartNonRepairableDetailHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <PartRepairableDetailHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <PrtReportingDetailHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <PartUsageDetailHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <SotDetailHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <RrtReportingDetailHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <MachineOthSotDetailHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <MachinePmSotDetailHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <MachineCmSotDetailHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
          </>
        )}
        {data.export_type && data.export_type == "summary" && (
          <>
            <AssetSummaryHandler
              currentExport={currentExport}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <PartRepairableSummaryHandler
              currentExport={currentExport}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <PartNonRepairableSummaryHandler
              currentExport={currentExport}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <PartUsageSummaryHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <PrtReportingSummaryHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <RrtReportingSummaryHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
          </>
        )}
        {dataSave.machines && (
          <>
            <MachineOthSotDetailHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <MachinePmSotDetailHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
            <MachineCmSotDetailHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
          </>
        )}
        {dataSave.service_order_tickets && (
          <>
            <SotDetailHandler
              currentExport={currentExport}
              data={data}
              dataSave={dataSave}
              exportToExcel={exportToExcel}
            />
          </>
        )}
      </div>
    </div>
  );
}
